import { useEffect, useState } from 'react';
import { BeakerIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type EggPredictionSnapshot } from '../../api/genetics';

interface EggHatchPredictionPanelProps {
  eggId: string;
  compact?: boolean;
}

export default function EggHatchPredictionPanel({ eggId, compact }: EggHatchPredictionPanelProps) {
  const [snapshot, setSnapshot] = useState<EggPredictionSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eggId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await geneticsApi.getEggPrediction(eggId);
        if (!cancelled) setSnapshot(result);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load hatch prediction');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [eggId]);

  if (loading) {
    return <p className="text-xs text-slate-500">Loading hatch-time expectations…</p>;
  }

  if (error) {
    return <p className="text-xs text-red-600">{error}</p>;
  }

  if (!snapshot?.predictionJson?.combinedPhenotypes?.length) {
    return compact ? null : (
      <p className="text-xs text-slate-500">No hatch-time prediction recorded for this egg.</p>
    );
  }

  const top = snapshot.predictionJson.combinedPhenotypes.slice(0, compact ? 3 : 5);
  const hasOutcome = snapshot.matched !== null && snapshot.matched !== undefined;

  return (
    <div className={`rounded-lg border border-violet-200 bg-violet-50/50 ${compact ? 'p-2' : 'p-3'}`}>
      <h5 className={`font-medium text-violet-900 flex items-center gap-1.5 ${compact ? 'text-xs mb-1' : 'text-sm mb-2'}`}>
        <BeakerIcon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        Hatch-time expectations
      </h5>

      <div className="space-y-1">
        {top.map((item) => (
          <div
            key={item.phenotype}
            className={`flex justify-between gap-2 bg-white rounded border border-violet-100 ${compact ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1'}`}
          >
            <span className="text-slate-800 truncate">{item.phenotype}</span>
            <span className="font-medium text-violet-700 flex-shrink-0">{(item.probability * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>

      {hasOutcome && (
        <div
          className={`mt-2 flex items-start gap-1.5 text-xs rounded p-1.5 ${
            snapshot.matched ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-50 text-amber-800 border border-amber-200'
          }`}
        >
          {snapshot.matched ? (
            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          )}
          <span>
            {snapshot.comparisonMessage ||
              (snapshot.matched ? 'Banded phenotype matches prediction.' : 'Banded phenotype differs from top prediction.')}
          </span>
        </div>
      )}
    </div>
  );
}
